import { useEffect, useState } from 'react';
import { useRoadmapService } from '../../../../services';

export interface MilestoneItem {
  _uid: string;
  name: string;
  color: string;
  start_date: any;
  id?: number;
}

interface UseMilestoneManagementProps {
  currentRoadmapId: string;
  singleRoadmapRes: any;
  projects: any[];
}

export const useMilestoneManagement = ({
  currentRoadmapId,
  singleRoadmapRes,
  projects,
}: UseMilestoneManagementProps) => {
  const [milestonesState, setMilestonesState] = useState<MilestoneItem[]>([]);
  const [isMilestoneActionLoading, setIsMilestoneActionLoading] = useState(false);
  const [isAddingMilestone, setIsAddingMilestone] = useState(false);
  const [addMilestone, setAddMilestone] = useState<MilestoneItem>({ _uid: '', name: '', color: '#000000', start_date: null });
  const [editMilestone, setEditMilestone] = useState<MilestoneItem | null>(null);

  const { updateRoadmap, fetchSingleRoadmap } = useRoadmapService();

  useEffect(() => {
    const source = Array.isArray(singleRoadmapRes?.milestones) ? singleRoadmapRes.milestones : [];
    // Normalize to ensure _uid
    const normalized = source.map((m: any) => ({
      _uid: m._uid || `${Date.now()}_${Math.floor(Math.random()*1e6)}`,
      name: m.name || '',
      color: m.color || '#000000',
      start_date: m.start_date ?? null,
      id: m.id,
    }));
    setMilestonesState(normalized);
  }, [singleRoadmapRes?.milestones]);

  const buildDependenciesPayload = (roadmapData: any) => {
    // Derive dependency payload from roadmap projects list if present
    const list = Array.isArray(roadmapData?.projects) ? roadmapData.projects : projects;
    if (!Array.isArray(list)) return [];
    return list
      .filter((p: any) => Array.isArray(p.dependencies) && p.dependencies.length > 0)
      .map((p: any) => ({
        dependency: p.dependencies.map((d: any) => {
          const depProjectId = d?.project_id || d?.value;
          const depProject = list.find((x: any) => x.project_id === depProjectId) || {};
          const projectName = d?.project_name || d?.label || depProject?.project_name || '';
          return {
            label: projectName,
            project_end_date: d?.project_end_date || depProject?.end_date || '',
            project_id: depProjectId,
            project_start_date: d?.project_start_date || depProject?.start_date || '',
            value: depProjectId,
          };
        }),
        project_end_date: p?.end_date,
        project_id: p?.project_id,
        project_start_date: p?.start_date,
      }));
  };

  const persistMilestones = async (nextMilestones: MilestoneItem[]) => {
    if (!currentRoadmapId) return;
    setIsMilestoneActionLoading(true);
    try {
      const payload = {
        name: singleRoadmapRes?.name || '',
        description: singleRoadmapRes?.description || '',
        projects: (singleRoadmapRes?.projects || []).map((p: any) => p.project_id),
        dependencies: buildDependenciesPayload(singleRoadmapRes),
        milestones: nextMilestones.map((m) => ({
          _uid: m._uid,
          name: m.name,
          color: m.color || '#000000',
          start_date: m.start_date,
          id: m.id || 0,
        })),
      };
      await updateRoadmap(currentRoadmapId, payload);
      await fetchSingleRoadmap(currentRoadmapId, false);
    } finally {
      setIsMilestoneActionLoading(false);
    }
  };

  const handleAddMilestone = async () => {
    if (!addMilestone?.name || !addMilestone?.start_date) return;
    const withId = { ...addMilestone, _uid: `${Date.now()}_${Math.floor(Math.random()*1e6)}` } as MilestoneItem;
    const next = [...milestonesState, withId];
    await persistMilestones(next);
    setMilestonesState(next);
    setIsAddingMilestone(false);
    setAddMilestone({ _uid: '', name: '', color: '#000000', start_date: null });
  };

  const handleUpdateMilestone = async () => {
    if (!editMilestone || !editMilestone.name || !editMilestone.start_date) return;
    const next = milestonesState.map((m) => (m._uid === editMilestone._uid ? editMilestone : m));
    await persistMilestones(next);
    setMilestonesState(next);
    setEditMilestone(null);
  };

  const handleDeleteMilestone = async (uid: string) => {
    const next = milestonesState.filter((m) => m._uid !== uid);
    await persistMilestones(next);
    setMilestonesState(next);
  };

  return {
    milestonesState,
    isMilestoneActionLoading,
    isAddingMilestone,
    setIsAddingMilestone,
    addMilestone,
    setAddMilestone,
    editMilestone,
    setEditMilestone,
    handleAddMilestone,
    handleUpdateMilestone,
    handleDeleteMilestone,
  };
};


